<template>
  <div class="pb-5">
    <div class="d-flex align-center px-3 mb-8">
      <div class="mr-4 pointer" @click="$router.back()">
        <v-img src="@/assets/images/svg/back-arrow.svg" width="20"></v-img>
      </div>
      <h3 class="mb-0">Super Save</h3>
    </div>

    <v-container v-if="supersaveData">
      <v-row>
        <v-col cols="12" md="5">
          <div class="white pa-4">
            <h3 class="mb-4">Savings details</h3>

            <label for="savings-purpose">Savings Purpose</label>
            <v-select
              id="savings-purpose"
              v-model="form.purpose"
              :items="purposeList"
              :disabled="loading"
              :loading="loading"
              :rules="[rules.required]"
              placeholder="Select Purpose For Saving"
              outlined
            ></v-select>

            <label for="savings-target">
              Savings Target (How much would you like to save?)
            </label>
            <fj-base-input
              id="savings-target"
              v-model="form.savings_target"
              :rules="savingsTargetRules"
              placeholder="Enter Savings Target"
              type="number"
            />

            <label for="savings-amount">Initial Amount</label>
            <fj-base-input
              id="savings-amount"
              v-model="form.amount"
              :rules="amountRules"
              placeholder="Enter Amount"
              type="number"
            />

            <small>
              <v-icon color="#f75116">mdi-information-outline</v-icon>
              Super Save has a fixed duration of 12 months.
            </small>
          </div>

          <div class="white pa-5 mt-5">
            <div class="payment">
              <h3 class="mb-4">Payment Method</h3>
              <div v-if="cardDetails && cardDetails.length">
                <div
                  v-for="card in cardDetails"
                  :key="card.id"
                  class="details mb-4"
                  :class="{ active: selected == card.id }"
                  @click="selectedCard(card.id)"
                >
                  <h3 class="mb-0">**** **** **** {{ card.last4 }}</h3>
                  <small class="d-block">{{ card.bank }}</small>
                </div>
              </div>
              <div
                class="py-5 text-center details font-weight-bold mb-4"
                :class="{ active: selected == 'new' }"
                @click="selectedCard('new')"
              >
                Pay via Paystack
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="5" class="d-flex flex-column">
          <div class="white pa-5 blur">
            <h3 class="mb-4">Overview</h3>
            <div class="mb-10">
              <div class="d-flex justify-space-between align-center pb-4">
                <small>Initial Amount</small>
                <p class="font-weight-bold mb-0">
                  NGN {{ formatAsMoney(form.amount || 0) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center pb-4">
                <small>Savings Target</small>
                <p class="font-weight-bold mb-0">
                  NGN {{ formatAsMoney(form.savings_target || 0) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center pb-4">
                <small>Duration</small>
                <p class="font-weight-bold mb-0">12 Months</p>
              </div>
              <div class="d-flex justify-space-between align-center pb-4">
                <small>Purpose</small>
                <p class="font-weight-bold mb-0">{{ form.purpose }}</p>
              </div>
              <div class="d-flex justify-space-between align-center pb-4">
                <small>Start Date</small>
                <p class="font-weight-bold mb-0">{{ startDate }}</p>
              </div>
              <div class="d-flex justify-space-between align-center pb-4">
                <small>End Date</small>
                <p class="font-weight-bold mb-0">{{ endDate }}</p>
              </div>
            </div>

            <small class="note mb-9">
              <b>Note:</b> Your Super Save wallet is activated only after
              payment is confirmed.
            </small>
            <fj-base-button
              :disabled="!isComplete"
              :loading="initiate"
              block
              @click="startSavings"
            >
              Pay Now
            </fj-base-button>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <fj-page-loader v-else />

    <svg id="svg-filter">
      <filter id="svg-blur">
        <feGaussianBlur
          in="SourceGraphic"
          :stdDeviation="!isComplete ? 4 : 0"
        ></feGaussianBlur>
      </filter>
    </svg>

    <paystack
      :paystackkey="$paystackKey"
      :amount="Number(paystackDetails.amount)"
      :email="paystackDetails.email"
      :reference="paystackDetails.reference"
      :channels="paystackDetails.channels"
      :callback="verify"
      :close="close"
    >
      Make Payment
    </paystack>

    <fj-response-modal
      :openModal="openModal"
      :status="status"
      :title="title"
      :message="message"
      callbackUrl="Super Save"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import paystack from "vue-paystack";
import FjResponseModal from "../../../components/modals/FjResponseModal";
import { formatAsMoney, formatDate } from "@/utils/helpers";
import rules from "@/utils/rules";

export default {
  components: {
    FjResponseModal,
    paystack,
  },
  data() {
    return {
      rules,
      loading: false,
      initiate: false,
      openModal: false,
      selected: null,
      status: "",
      message: "",
      title: "",
      purposeList: [],
      form: {
        amount: null,
        purpose: "",
        savings_target: null,
      },
      paystackDetails: {
        amount: 0,
        reference: "",
        email: "",
        channels: [],
      },
    };
  },
  computed: {
    ...mapState(["cardDetails"]),
    ...mapState({
      supersaveData: "Supersave",
    }),
    paymentMethod() {
      return this.selected === "new" ? "payonline" : "paybycard";
    },
    minimumAmount() {
      return Number(
        this.supersaveData?.walletDetails?.savings?.monthly?.minimum || 20000
      );
    },
    minimumSavingsTarget() {
      return Number(
        this.supersaveData?.walletDetails?.min_savings_target || 0
      );
    },
    amountRules() {
      return [
        value => !!value || "Required",
        value => Number(value) >= this.minimumAmount ||
          `Minimum amount of NGN ${this.minimumAmount}`,
      ];
    },
    savingsTargetRules() {
      return [
        value => !!value || "Required",
        value => Number(value) > 0 || "Invalid number",
        value => Number(value) >= this.minimumSavingsTarget ||
          `Minimum amount of NGN ${this.minimumSavingsTarget}`,
      ];
    },
    isComplete() {
      return Boolean(
        this.form.purpose &&
        Number(this.form.amount) >= this.minimumAmount &&
        Number(this.form.savings_target) > 0 &&
        Number(this.form.savings_target) >= this.minimumSavingsTarget &&
        this.selected
      );
    },
    startDate() {
      return formatDate(new Date());
    },
    endDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return formatDate(date);
    },
  },
  async mounted() {
    await this.loadForm();
  },
  methods: {
    ...mapActions({
      getCards: "cards/getCards",
      getSupersave: "supersave/getSupersave",
      startSupersave: "supersave/startSupersave",
      verifyPayments: "payments/verifyPayments",
      fetchPurposeList: "user/getSavingsPurpose",
    }),
    async loadForm() {
      try {
        this.loading = true;
        const requests = [this.fetchPurposeList(), this.getCards()];
        if (!this.supersaveData) requests.push(this.getSupersave());
        const [purposeList] = await Promise.all(requests);
        this.purposeList = purposeList;
      } finally {
        this.loading = false;
      }
    },
    selectedCard(cardId) {
      this.selected = cardId;
    },
    async startSavings() {
      try {
        this.initiate = true;
        const payload = {
          amount: Number(this.form.amount),
          purpose: this.form.purpose,
          savings_target: Number(this.form.savings_target),
          request: this.paymentMethod,
        };

        if (this.paymentMethod === "paybycard") {
          payload.cardauthid = this.selected;
        }

        const response = await this.startSupersave(payload);
        if (this.paymentMethod === "payonline") {
          this.initializePaystack(response);
        } else if (response.payload.payment) {
          this.successMessage();
          this.refreshAccount();
        } else {
          this.errorMessage(response.payload.message);
          this.refreshAccount();
        }
      } catch (error) {
        this.errorMessage();
      } finally {
        this.initiate = false;
      }
    },
    initializePaystack(response) {
      this.paystackDetails.reference = response.payload.reference;
      this.paystackDetails.amount = response.payload.amount;
      this.paystackDetails.email = response.payload.email;
      this.paystackDetails.channels = response.payload.payment_channel;
      document.querySelector(".payButton").click();
    },
    async verify(data) {
      try {
        const response = await this.verifyPayments({
          reference: data.reference,
        });
        if (response.payload.paymentSuccess) {
          this.successMessage();
        } else {
          this.errorMessage(response.payload.message);
        }
      } catch (error) {
        this.errorMessage();
      } finally {
        this.refreshAccount();
      }
    },
    async close() {
      try {
        await this.verifyPayments({
          reference: this.paystackDetails.reference,
        });
      } catch (error) {
        // The verification request reports the payment failure to the backend.
      } finally {
        this.errorMessage();
        this.refreshAccount();
      }
    },
    refreshAccount() {
      setTimeout(() => {
        this.getSupersave();
        this.$store.dispatch("user/fetchUser");
      }, 4000);
    },
    successMessage() {
      this.title = "Transaction Successful";
      this.message =
        "Your Super Save payment was successful. Your account will update after payment confirmation.";
      this.status = "success";
      this.openModal = true;
    },
    errorMessage(message) {
      this.title = "Something went wrong";
      this.message = message ||
        "Your Super Save payment was not verified. Please check the details and try again.";
      this.status = "failed";
      this.openModal = true;
    },
    formatAsMoney(amount) {
      return formatAsMoney(Number(amount));
    },
  },
};
</script>

<style lang="scss" scoped>
.note {
  line-height: 1.6;
  display: block;
}

.payment {
  .details {
    width: 100%;
    border: 1px solid #000;
    padding: 14px 24px;
    cursor: pointer;

    small {
      display: block;
      margin-top: -6px;
    }

    &.active {
      border: 1px solid var(--primary);
      background: rgba(247, 81, 22, 0.1);
    }
  }
}

.blur {
  -webkit-filter: url(#svg-blur);
  filter: url(#svg-blur);
}
</style>
